import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../../../styles/theme';
import { glassDeep, glassMedium } from '../../../../styles/components/common/glassmorphism';
import { fadeInAnimation, pulseAnimation } from '../../../../styles/components/common/animations';

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-height: ${props => props.isLive ? '70vh' : '65vh'}; /* Increased height for pre-recorded videos */
  background: ${({ theme: propTheme }) => (propTheme || theme).colors.textPrimary};
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.lg};
  overflow: hidden;
  margin-bottom: ${({ isLive, theme: propTheme }) => isLive ? '0' : (propTheme || theme).spacing.md};
  box-shadow: ${({ theme: propTheme }) => (propTheme || theme).shadows.lg};
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${({ theme: propTheme }) => (propTheme || theme).shadows.xl};
  }
  
  @media (max-width: ${({ theme: propTheme }) => (propTheme || theme).breakpoints.md}) {
    max-height: ${({ isLive, theme: propTheme }) => isLive ? '50vh' : '55vh'}; /* Increased height on medium screens */
  }
  
  @media (max-width: ${({ theme: propTheme }) => (propTheme || theme).breakpoints.sm}) {
    max-height: ${({ isLive, theme: propTheme }) => isLive ? '50vh' : '50vh'};
  }
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.isLive ? 'contain' : 'cover'}; /* Use cover for pre-recorded videos */
  background: #000;
  min-height: ${props => props.isLive ? 'auto' : '400px'}; /* Minimum height for pre-recorded videos */
`;

const Controls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.9), transparent);
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  opacity: 0;
  transition: opacity 0.3s ease;
  ${glassMedium}
  
  ${VideoContainer}:hover & {
    opacity: 1;
  }
`;

const Button = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
  cursor: pointer;
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.sm};
  transition: all 0.3s ease;
  ${glassDeep}

  svg {
    width: 24px;
    height: 24px;
    transition: transform 0.3s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
    
    svg {
      transform: scale(1.1);
    }
  }
  
  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Progress = styled.input`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: rgba(255,255,255,0.2);
  cursor: pointer;
  -webkit-appearance: none;
  transition: all 0.3s ease;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  }

  &::-webkit-slider-runnable-track {
    height: 6px;
    background: rgba(255,255,255,0.2);
    border-radius: 3px;
  }

  &:hover {
    height: 8px;
    
    &::-webkit-slider-thumb {
      transform: scale(1.2);
    }
  }
`;

const Time = styled.div`
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.sm};
  font-variant-numeric: tabular-nums;
  min-width: 120px;
  text-align: right;
  font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.medium};
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  background: rgba(0, 0, 0, 0.3);
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.sm};
  ${glassDeep}
`;

const LiveIndicator = styled.div`
  position: absolute;
  top: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  left: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  background: ${({ theme: propTheme }) => (propTheme || theme).colors.error};
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
  padding: ${({ theme: propTheme }) => `${(propTheme || theme).spacing.xs} ${(propTheme || theme).spacing.md}`};
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.pill};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.sm};
  font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.medium};
  display: flex;
  align-items: center;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  animation: ${fadeInAnimation} 0.5s ease-out;
  ${glassDeep}

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
    border-radius: 50%;
    animation: ${pulseAnimation} 2s infinite;
  }
`;

const formatTime = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const VideoPlayer = React.forwardRef(({ src, isLive, poster, onTimeUpdate, ...props }, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const internalRef = useRef(null);
  const videoRef = ref || internalRef;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoaded(true);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      // Cleanup video element when component unmounts
      if (video) {
        video.pause();
        video.src = '';
        video.load();
      }
    };
  }, [videoRef, onTimeUpdate, src]);

  const handlePlayPause = async () => {
    try {
      if (videoRef.current.paused) {
        await videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    } catch (error) {
      console.warn('Playback error:', error);
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    // Do nothing when stop button is clicked
    // Button is clickable but doesn't perform any action
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      onTimeUpdate?.(videoRef.current.currentTime);
    }
  };

  const handleProgress = (e) => {
    if (!videoRef.current) return;
    const time = parseFloat(e.target.value);
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  return (
    <VideoContainer isLive={isLive} {...props}>
      <Video
        ref={videoRef}
        src={src}
        poster={poster}
        onTimeUpdate={handleTimeUpdate}
        playsInline
        isLive={isLive}
      />
      {isLive && <LiveIndicator>In Progress</LiveIndicator>}
      <Controls>
        <Button 
          onClick={handlePlayPause}
        >
          {isPlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </Button>
        <Button 
          onClick={handleStop}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 6h12v12H6z"/>
          </svg>
        </Button>
        <Progress
          type="range"
          min="0"
          max={duration || 0}
          value={currentTime}
          onChange={handleProgress}
          disabled={!isLoaded}
        />
        <Time>{formatTime(currentTime)} / {formatTime(duration)}</Time>
      </Controls>
    </VideoContainer>
  );
});

export default VideoPlayer;
